import React, { useCallback } from "react";
import styled from "styled-components";

import { getRecommendationUrl } from "../../helpers/areenaLinks";
import { playRecommendation } from "../../helpers/recommendations";
import { Recommendation } from "../../service/areena-recommendations";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { PrimaryLink } from "../../styles/global";
import { focusColor, recommendationTextColor } from "../../styles/variables";
import { PlayerUpdateEvent } from "../../types/public";
import { RecommendationCard } from "../recommendations/RecommendationCard";
import {
  BackgroundGradient,
  openSansBold,
  RecommendationBackground,
  RecommendationCardTitle,
  RecommendationItemBackground,
  RecommendationList,
  TextShadow,
  watchNext
} from "../recommendations/RecommendationStyles";
import {
  CommonCardProps,
  SetPlayingRecommendation,
  UiSize
} from "../recommendations/recommendationTypes";

interface ResponsiveProps {
  uiSize: UiSize;
  newPlayerUi?: boolean;
}

const NormalRecommendationList = styled(RecommendationList)<{
  newPlayerUi: boolean;
}>`
  bottom: 0;
  left: 0;
  margin: ${props =>
    props.newPlayerUi ? "0 20px 80px 20px" : "0 20px 60px 20px"};
  position: absolute;
  right: 0;
`;

const LargeRecommendation = styled.div<ResponsiveProps>`
  left: 20px;
  position: absolute;
  top: 0;
  width: 64%;
  margin-top: ${props =>
    props.newPlayerUi ? "56px" : props.uiSize === UiSize.Large ? "3em" : "1em"};
`;

const LargeRecommendationText = styled(TextShadow)`
  color: ${recommendationTextColor};
`;

const WatchMainRecommendationLink = styled(PrimaryLink)`
  font-size: 1.125em;
  padding: 0.3em 1em;

  &:focus-visible {
    outline: 1px solid ${focusColor};
  }
`;

const DescriptionText = styled.div<ResponsiveProps>`
  margin-bottom: ${props =>
    props.uiSize === UiSize.Large ? "1.4em" : "1.3em"};
`;

const RecommendationTitle = styled.span<ResponsiveProps>`
  &&& {
    display: block;
    height: 2.4em;
    line-height: 1.2em;
    margin: 0.3em 0;
    overflow: hidden;
    font-size: ${props => (props.uiSize === UiSize.Large ? "2.25em" : "2em")};
    ${openSansBold}
  }
`;

const LargeRecommendationCard = ({
  language,
  newPlayerUi,
  recommendation,
  setPlayingRecommendation,
  uiSize,
  update,
  watchNextString
}: {
  language: string;
  newPlayerUi: boolean;
  recommendation: Recommendation;
  setPlayingRecommendation: SetPlayingRecommendation;
  uiSize: UiSize;
  update: PlayerUpdateEvent;
  watchNextString: string;
}) => {
  const { getState } = usePlayerStore();
  const url = getRecommendationUrl({
    index: 0,
    language,
    programId: recommendation.id
  });

  const onClickPlay = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const programId = getState().programId;
      if (!programId) return;

      playRecommendation({
        index: 0,
        language,
        mainRecommendation: true,
        playProgram: getState().isFullscreen,
        programId,
        recommendation,
        setPlayingRecommendation,
        update
      });
    },
    [getState, language, recommendation, setPlayingRecommendation, update]
  );

  const showButtonAriaLabel = `${i18n("nayta_ohjelma", { language })} ${
    recommendation.title
  }`;

  return (
    <LargeRecommendation newPlayerUi={newPlayerUi} uiSize={uiSize}>
      <LargeRecommendationText>
        <RecommendationCardTitle>{watchNextString}</RecommendationCardTitle>
        <RecommendationTitle uiSize={uiSize}>
          {recommendation.title}
        </RecommendationTitle>
        <DescriptionText uiSize={uiSize}>
          {recommendation.description}
        </DescriptionText>
        <WatchMainRecommendationLink
          aria-label={showButtonAriaLabel}
          href={url}
          onClick={onClickPlay}>
          {i18n("nayta_ohjelma", { language })}
        </WatchMainRecommendationLink>
      </LargeRecommendationText>
    </LargeRecommendation>
  );
};

export const LargeEndRecommendations = ({
  commonCardProps,
  recommendations
}: {
  recommendations: Recommendation[];
  commonCardProps: CommonCardProps;
}) => (
  <RecommendationBackground>
    <RecommendationItemBackground image={recommendations[0].image} />
    <BackgroundGradient />
    <LargeRecommendationCard
      {...{
        ...commonCardProps,
        recommendation: recommendations[0],
        watchNextString: watchNext(commonCardProps.language)
      }}
    />
    <NormalRecommendationList newPlayerUi={commonCardProps.newPlayerUi}>
      {recommendations.slice(1).map((recommendation, index) => (
        <RecommendationCard
          key={`end-recommendation-${index}`}
          {...{
            ...commonCardProps,
            index,
            recommendation,
            showDescription: true
          }}
        />
      ))}
    </NormalRecommendationList>
  </RecommendationBackground>
);
